import { useEffect, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, Loader2, Sparkles, LineChart } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";
import { Switch } from "./ui/switch";

function GrowthBadge({ pct }) {
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{up ? "+" : ""}{pct}%
    </span>
  );
}

export const ForecastPanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    const attempt = async (n) => {
      try {
        const r = await api.get("/admin/forecast");
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch (e) {
        if (cancelled) return;
        if (n < 3) { setTimeout(() => attempt(n + 1), 2000); return; }
        setData((prev) => (prev && !prev.error ? prev : { error: true }));
        setLoading(false);
      }
    };
    attempt(1);
    return () => { cancelled = true; };
  }, [refreshing, enabled]);

  const Toggle = () => (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-white p-5" data-testid="forecast-toggle-row">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#faf7ee] flex items-center justify-center text-[#8a681c] shrink-0"><LineChart className="w-4 h-4" /></div>
        <div>
          <p className="font-display font-bold">Sales projection</p>
          <p className="text-sm text-muted-foreground">Off by default. Turn on to build a forecast from WooCommerce history.</p>
        </div>
      </div>
      <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="forecast-toggle" />
    </div>
  );

  if (!enabled) return <div className="space-y-6" data-testid="forecast-panel"><Toggle /></div>;
  if (loading) return <div className="space-y-6" data-testid="forecast-panel"><Toggle /><div className="card-soft rounded-2xl p-12 flex items-center justify-center gap-3 text-muted-foreground" data-testid="forecast-loading"><Loader2 className="w-5 h-5 animate-spin" /> Building forecast from WooCommerce history…</div></div>;
  if (!data || data.configured === false) return <div className="space-y-6" data-testid="forecast-panel"><Toggle /><div className="card-soft rounded-2xl p-8 text-muted-foreground" data-testid="forecast-unconfigured">WooCommerce is not connected.</div></div>;
  if (data.error) return <div className="space-y-6" data-testid="forecast-panel"><Toggle /><div className="card-soft rounded-2xl p-8 text-muted-foreground" data-testid="forecast-error">Couldn't reach WooCommerce right now. Try again in a moment.</div></div>;

  const chartData = [
    { label: "Last 30d (actual)", revenue: data.historic.revenue, orders: data.historic.orders, actual: true },
    ...data.monthly.map((m) => ({ label: m.label, revenue: m.revenue, orders: m.orders })),
  ];

  return (
    <div className="space-y-6" data-testid="forecast-panel">
      <Toggle />
      <div className="flex items-start gap-3 rounded-xl border border-[#dfd0a9] bg-[#faf7ee] p-4">
        <Sparkles className="w-5 h-5 shrink-0 text-[#8a681c] mt-0.5" />
        <p className="text-sm text-muted-foreground">{data.method}</p>
      </div>

      {/* Baseline + trend */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="card-soft rounded-2xl p-6" data-testid="forecast-baseline">
          <p className="eyebrow">Historic baseline — last 30 days</p>
          <div className="flex items-end justify-between mt-3">
            <div><p className="font-display font-extrabold text-2xl">{formatMoney(data.historic.revenue)}</p><p className="text-xs text-muted-foreground">{data.historic.orders} orders · {formatMoney(data.historic.daily_revenue)}/day</p></div>
          </div>
        </div>
        <div className="card-soft rounded-2xl p-6" data-testid="forecast-trend">
          <p className="eyebrow">Trailing 14-day run-rate</p>
          <div className="flex items-end justify-between mt-3">
            <div><p className="font-display font-extrabold text-2xl">{formatMoney(data.trailing_14d.daily_revenue)}<span className="text-sm text-muted-foreground font-normal">/day</span></p><p className="text-xs text-muted-foreground">{data.trailing_14d.daily_orders} orders/day</p></div>
            <div className="text-right"><p className="text-xs text-muted-foreground">Revenue trend</p><GrowthBadge pct={data.trailing_14d.revenue_growth_pct} /></div>
          </div>
        </div>
      </div>

      {/* Forecast cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {data.forecasts.map((f) => (
          <div key={f.horizon_days} className="card-soft rounded-2xl p-6" data-testid={`forecast-${f.horizon_days}`}>
            <p className="eyebrow">Next {f.horizon_days} days</p>
            <p className="font-display font-extrabold text-3xl mt-3" style={{ color: "var(--gold)" }}>{formatMoney(f.revenue)}</p>
            <p className="text-sm text-muted-foreground mt-1" data-testid={`forecast-orders-${f.horizon_days}`}>~{f.orders.toLocaleString()} orders projected</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="card-soft rounded-2xl p-6">
        <h3 className="font-display font-bold mb-4">Projected revenue &amp; orders by month</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" stroke="#9c9488" fontSize={11} />
            <YAxis yAxisId="left" stroke="#9c9488" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <YAxis yAxisId="right" orientation="right" stroke="#9c9488" fontSize={11} />
            <Tooltip formatter={(v, n) => (n === "revenue" ? formatMoney(v) : v)} contentStyle={{ background: "#fff", border: "1px solid #e2dcd0", borderRadius: 12 }} />
            <Legend />
            <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#c0952f" radius={[6, 6, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
